import { useEffect, useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import {
  Home, Users, FileText, Coins, Clock, ShieldCheck, CheckSquare,
  Sparkles, Layers, Settings, ChevronsLeft, ChevronsRight,
  Briefcase, Target, BarChart3, Shield, Globe2, Calculator, Timer, UserCircle, ClipboardList, LineChart, GraduationCap,
} from 'lucide-react';
import { useCurrentUser } from '@/lib/auth';
import { useMyCompany } from '@/lib/companies';
import { cn } from '@/lib/cn';
import { LanguageSwitcher } from '@/components/layout/LanguageSwitcher';
import { NotificationBell } from './NotificationBell';
import { UserMenu } from './UserMenu';
import { UnverifiedEmailBanner } from './UnverifiedEmailBanner';

type NavItem = { to: string; key: string; Icon: React.ComponentType<{ className?: string }> };

const NAV_ITEMS: NavItem[] = [
  { to: '/app',             key: 'home',        Icon: Home },
  { to: '/app/me',          key: 'mySpace',     Icon: UserCircle },
  { to: '/app/people',      key: 'people',      Icon: Users },
  { to: '/app/hiring',      key: 'hiring',      Icon: Briefcase },
  { to: '/app/performance', key: 'performance', Icon: Target },
  { to: '/app/surveys',     key: 'surveys',     Icon: ClipboardList },
  { to: '/app/learning',    key: 'learning',    Icon: GraduationCap },
  { to: '/app/documents',   key: 'documents',   Icon: FileText },
  { to: '/app/payroll',     key: 'payroll',     Icon: Coins },
  { to: '/app/year-end',    key: 'yearEnd',     Icon: Calculator },
  { to: '/app/time-off',    key: 'timeOff',     Icon: Clock },
  { to: '/app/attendance',  key: 'attendance',  Icon: Timer },
  { to: '/app/compliance',  key: 'compliance',  Icon: ShieldCheck },
  { to: '/app/approvals',   key: 'approvals',   Icon: CheckSquare },
  { to: '/app/agents',      key: 'agents',      Icon: Sparkles },
  { to: '/app/border-buddy',key: 'borderBuddy', Icon: Globe2 },
  { to: '/app/departments', key: 'departments', Icon: Layers },
  { to: '/app/reports',     key: 'reports',     Icon: BarChart3 },
  { to: '/app/analytics',   key: 'analytics',   Icon: LineChart },
  { to: '/app/settings',    key: 'settings',    Icon: Settings },
];

// Staff-only nav — appended dynamically inside the component once the
// current user is loaded. The admin dashboard is the primary entry
// point; users / companies / inbox are deep links inside it.
const STAFF_NAV: NavItem[] = [
  { to: '/admin',           key: 'adminDashboard', Icon: Shield },
  { to: '/admin/users',     key: 'adminUsers',     Icon: Users },
  { to: '/admin/companies', key: 'adminCompanies', Icon: Briefcase },
  { to: '/admin/inbox',     key: 'adminInbox',     Icon: BarChart3 },
];

export function AppShell({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  const user = useCurrentUser();
  const company = useMyCompany();
  const nav = useNavigate();
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem('keenez.shell.collapsed') === '1';
  });

  useEffect(() => {
    if (user.isSuccess && !user.data) nav('/', { replace: true });
    if (user.data && company.isSuccess && !company.data) nav('/onboarding', { replace: true });
  }, [user.isSuccess, user.data, company.isSuccess, company.data, nav]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem('keenez.shell.collapsed', collapsed ? '1' : '0');
  }, [collapsed]);

  if (user.isLoading || company.isLoading) {
    return (
      <div className="min-h-screen grid place-items-center text-muted-foreground text-sm">
        <div className="flex items-center gap-3">
          <span className="h-2 w-2 rounded-full bg-primary animate-pulse" />
          {t('common.loading')}
        </div>
      </div>
    );
  }
  if (!user.data || !company.data) {
    return <div className="min-h-screen grid place-items-center text-muted-foreground text-sm">{t('common.redirecting')}</div>;
  }

  return (
    <div className="min-h-screen flex bg-muted/30">
      <aside
        className={cn(
          'relative shrink-0 border-e border-border bg-card/80 backdrop-blur-md flex flex-col transition-[width] duration-300 ease-out',
          collapsed ? 'w-[68px]' : 'w-64',
        )}
      >
        {/* Brand */}
        <Link to="/app" className="flex items-center gap-2.5 px-4 h-16 border-b border-border">
          <ShellLogo />
          {!collapsed && (
            <span className="font-display text-base font-semibold tracking-tight">{t('brand.name')}</span>
          )}
        </Link>

        {/* Primary nav */}
        <nav className="px-2.5 py-4 flex-1 space-y-0.5 overflow-y-auto keenez-scrollbar">
          {[...NAV_ITEMS, ...(user.data?.isStaff ? STAFF_NAV : [])].map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.to === '/app'}
              title={collapsed ? t(`appShell.${it.key}`) : undefined}
              className={({ isActive }) =>
                cn(
                  'group relative flex items-center gap-3 rounded-xl text-sm font-medium transition-all duration-200',
                  collapsed ? 'justify-center px-2 py-2.5' : 'px-3 py-2.5',
                  isActive
                    ? 'text-primary bg-primary/10 shadow-soft'
                    : 'text-foreground/70 hover:text-foreground hover:bg-muted/70',
                )
              }
            >
              {({ isActive }) => (
                <>
                  {isActive && (
                    <motion.span
                      layoutId="app-nav-active"
                      className="absolute start-0 top-1.5 bottom-1.5 w-1 rounded-e-full bg-gradient-to-b from-indigo-500 to-fuchsia-500"
                    />
                  )}
                  <it.Icon className={cn('h-4 w-4 shrink-0', isActive ? 'text-primary' : 'text-foreground/55 group-hover:text-foreground')} />
                  {!collapsed && <span className="truncate">{t(`appShell.${it.key}`)}</span>}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Footer — only the collapse toggle. User + workspace
            chrome live in the topbar, so the sidebar stays tight
            and focused on navigation. */}
        <div className="border-t border-border px-2.5 py-3">
          <button
            onClick={() => setCollapsed(!collapsed)}
            className={cn(
              'inline-flex h-8 items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-muted',
              collapsed ? 'w-full' : 'w-full',
            )}
            aria-label={collapsed ? 'expand' : 'collapse'}
          >
            {collapsed ? <ChevronsRight className="h-4 w-4" /> : <ChevronsLeft className="h-4 w-4" />}
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-x-hidden">
        {/* Topbar — workspace name on the left, language /
            notifications / user menu on the right. The user menu
            owns theme + profile + logout (no more sidebar footer
            profile card). */}
        <header className="sticky top-0 z-20 h-14 px-4 md:px-6 flex items-center justify-between gap-3 bg-background/80 backdrop-blur-md border-b border-border">
          <div className="min-w-0 flex items-center gap-3">
            <div className="hidden sm:block min-w-0">
              <p className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-semibold">
                {t('common.workspace', { defaultValue: 'Workspace' })}
              </p>
              <p className="text-[13.5px] font-semibold text-foreground truncate">{company.data.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2">
            <LanguageSwitcher variant="compact" />
            <NotificationBell />
            <UserMenu user={user.data} />
          </div>
        </header>
        <UnverifiedEmailBanner />
        {/* Subtle decorative gradient blob on every page. */}
        <div aria-hidden className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
          <div className="absolute top-0 end-0 h-[26rem] w-[26rem] rounded-full bg-gradient-to-br from-indigo-200/30 to-fuchsia-200/20 dark:from-indigo-500/10 dark:to-fuchsia-500/8 blur-3xl" />
          <div className="absolute bottom-0 start-1/3 h-[22rem] w-[22rem] rounded-full bg-gradient-to-br from-sky-200/20 to-emerald-200/15 dark:from-sky-500/8 dark:to-emerald-500/8 blur-3xl" />
        </div>
        {children}
      </main>
    </div>
  );
}

function ShellLogo() {
  return <img src="/logo.png" alt="KeenEZ" className="h-9 w-9 rounded-xl object-contain shrink-0" />;
}
